<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineEmits } from "vue";
import { Splide, SplideSlide } from "@splidejs/vue-splide";
import "@splidejs/vue-splide/css";
import { useBetBoosterStore, isBoostActive } from "@/stores/bet_booster";
import { usePreSportsStore } from "@/stores/pre_sports";
import { useSocketStore } from "@/stores/socket";
import BetBoosterCard from "./BetBoosterCard.vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
	section: {
		type: Object,
		default: null
	},
	showTitle: {
		type: Boolean,
		default: true
	},
	title: {
		type: String,
		default: ""
	},
	infoText: {
		type: String,
		default: ""
	},
	boosts: {
		type: Array,
		default: null
	},
	selected_sport_id: {
		type: [String, Number],
		default: null
	},
	sport_id: {
		type: [String, Number],
		default: null
	},
	fixture_id: {
		type: [String, Number],
		default: null
	},
	competition_id: {
		type: [String, Number, Array],
		default: null
	},
	formated_title: {
		type: Boolean,
		default: false
	}
});

const emit = defineEmits(["click"]);

const betBoosterStore = useBetBoosterStore();
const preSportsStore = usePreSportsStore();
const socketStore = useSocketStore();
const { t } = useI18n();
const splideRef = ref(null);
const show_tooltip = ref(false);
const direction = ref(document.dir);

const joined_fixture_ids = new Set();

const joinBoostedFixtures = () => {
	const fixtures = betBoosterStore.bet_boosted_fixtures || [];
	fixtures.forEach((f) => {
		const fixture_id = f.fixture_id;
		if (fixture_id && !joined_fixture_ids.has(fixture_id)) {
			socketStore.join("favouriteFixture", fixture_id);
			joined_fixture_ids.add(fixture_id);
		}
	});
	if (props.fixture_id) {
		const fId = Number(props.fixture_id);
		if (!isNaN(fId) && !joined_fixture_ids.has(fId)) {
			socketStore.join("favouriteFixture", fId);
			joined_fixture_ids.add(fId);
		}
	}
};

watch(() => props.fixture_id,(newId) => {
	if (newId) {
		const fId = Number(newId);
		if (!isNaN(fId) && !joined_fixture_ids.has(fId)) {
			socketStore.join("favouriteFixture", fId);
			joined_fixture_ids.add(fId);
		}
	}
},
{ immediate: true }
);

const leaveBoostedFixtures = () => {
	joined_fixture_ids.forEach((fixture_id) => {
		socketStore.leave("favouriteFixture", fixture_id);
	});
	joined_fixture_ids.clear();
};

const checkAndLeaveExpiredFixtures = () => {
	const configs = betBoosterStore.bet_boosted_fixtures || [];
	configs.forEach((config) => {
		const fixture_id = config.fixture_id;
		if (joined_fixture_ids.has(fixture_id)) {
			const fixture = betBoosterStore.fixtures_data[fixture_id];
			if (fixture && !isBoostActive(config, fixture)) {
				socketStore.leave("favouriteFixture", fixture_id);
				joined_fixture_ids.delete(fixture_id);
			}
		}
	});
};

const handleWindowClick = () => {
	show_tooltip.value = false;
};

onMounted(() => {
	window.addEventListener("click", handleWindowClick);

	betBoosterStore.startExpirationTimer();

	if (!betBoosterStore.bet_boosted_fixtures.length && !betBoosterStore.loading) betBoosterStore.fetchBoostedBets();

	joinBoostedFixtures();
});

onBeforeUnmount(() => {
	window.removeEventListener("click", handleWindowClick);
	betBoosterStore.stopExpirationTimer();
	leaveBoostedFixtures();
});

watch(() => betBoosterStore.bet_boosted_fixtures, (fixtures) => {
	if (fixtures && fixtures.length) joinBoostedFixtures();		
},
{ deep: true, immediate: true }
);

watch(() => betBoosterStore.fixtures_data, () => {
	checkAndLeaveExpiredFixtures();
},
{ deep: true }
);

watch(() => betBoosterStore.current_timestamp, () => {
	checkAndLeaveExpiredFixtures();
});

const headerTitle = computed(() => {
	if (props.section?.title) return props.section.title;
	if (props.title) return props.title;
	return t("bet_boost.title");
});

const tooltipText = computed(() => {
	if (props.infoText) return props.infoText;
	return t("bet_boost.info_tooltip");
});

const availableSports = computed(() => {
	return betBoosterStore.availableSports;
});

const hasSportIdProp = computed(() => {
	return props.selected_sport_id != null || props.sport_id != null || props.fixture_id != null || props.competition_id != null;
});

const selectedSportId = computed(() => {
	if (props.sport_id != null) return props.sport_id;
	if (props.selected_sport_id != null) return props.selected_sport_id;
	return betBoosterStore.selected_sport_id;
});

const isAllSportsSelected = computed(() => {
	return !selectedSportId.value || selectedSportId.value === "all";
});

const shouldShowCardSportRibbon = computed(() => {
	if (props.fixture_id != null || props.competition_id != null) return false;
	return !hasSportIdProp.value && isAllSportsSelected.value;
});

const displayedBoosts = computed(() => {
	if (props.boosts != null) return props.boosts;
	
	if (props.fixture_id != null) {
		return betBoosterStore.boosted_bets.filter(
			(item) => String(item.fixture_id || item.id) === String(props.fixture_id)
		);
	}

	const active_sport_id = props.sport_id != null
		? props.sport_id
		: (props.selected_sport_id != null ? props.selected_sport_id : betBoosterStore.selected_sport_id);

	if (props.competition_id != null && props.competition_id !== "") {
		const comp_ids = Array.isArray(props.competition_id) ? props.competition_id.map(String) : String(props.competition_id).split(',').map((s) => s.trim());

		return betBoosterStore.boosted_bets.filter((item) => {
			const matches_comp = comp_ids.includes(String(item.competition_id));
			if (!matches_comp) return false;

			if (active_sport_id && active_sport_id !== "all") return String(item.sport_id) === String(active_sport_id);
			
			return true;
		});
	}

	if (!active_sport_id || active_sport_id === "all") return [...betBoosterStore.boosted_bets].sort((a, b) => Number(a.sport_id) - Number(b.sport_id));
	
	return betBoosterStore.boosted_bets.filter(
		(item) => String(item.sport_id) === String(active_sport_id)
	);
});

watch(
	() => [props.selected_sport_id, props.sport_id],
	([newSelected, newSportId]) => {
		const target = newSportId != null ? newSportId : newSelected;
		if (target != null) {
			betBoosterStore.setSelectedSport(target);
			if (splideRef.value && splideRef.value.go) {
				splideRef.value.go(0);
			}
		}
	},
	{ immediate: true }
);

watch(() => props.competition_id,() => {
	if (splideRef.value && splideRef.value.go) splideRef.value.go(0);
});

const selectSport = (sportId) => {
	betBoosterStore.setSelectedSport(sportId);
	if (splideRef.value && splideRef.value.go) splideRef.value.go(0);
};

const toggleTooltip = () => {
	show_tooltip.value = !show_tooltip.value;
};

const handleCardClick = (boost) => {
	emit("click", boost);
};

const onTransitionEnd = () => {
	if (splideRef.value && splideRef.value.splide) {
		splideRef.value.splide.refresh();
	}
};

const isScopedMode = computed(() => props.fixture_id != null || props.competition_id != null);

const splideOptions = computed(() => {
	const is_scoped = isScopedMode.value;
	return {
		direction: direction.value,
		type: "slide",
		rewind: false,
		perPage: is_scoped ? 3 : 4,
		perMove: 1,
		gap: "12px",
		pagination: false,
		arrows: true,
		drag: true,
		trimSpace: "move",
		breakpoints: {
			1800: {
				perPage: is_scoped ? 3 : 4,
				gap: "12px"
			},
			1400: {
				perPage: is_scoped ? 3 : 3,
				gap: "12px"
			},
			1100: {
				perPage: is_scoped ? 2 : 3,
				gap: "10px"
			},
			991: {
				perPage: 2,
				gap: "10px"
			},
			425: {
				perPage: 1,
				gap: "10px"
			}
		}
	};
});
</script>

<template>
	<Transition name="bet-booster-slide" @after-enter="onTransitionEnd">
		<div v-if="displayedBoosts && displayedBoosts.length" class="bet-booster-container" :class="{ 'fixture-booster': props.fixture_id != null || props.competition_id != null }">
		<!-- Top Header Section (Title + Info Tooltip + Sports Filters) -->
		<div class="bet-booster-header">
			<div class="header-left">
				<div v-if="showTitle" class="title-with-info">
					<img v-if="props.section?.media?.path" :src="props.section.media.path" class="section-media-icon" alt="" />
					<h3 class="bet-booster-title" :class="{ 'formated-title': props.formated_title }">{{ headerTitle }}</h3>

					<!-- Info Tooltip -->
					<div class="info-tooltip-container" @mouseenter="show_tooltip = true" @mouseleave="show_tooltip = false" @click.stop="toggleTooltip">
						<button type="button" class="info-icon-btn" aria-label="Information">
							<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
								<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
							</svg>
						</button>

						<div v-if="show_tooltip" class="info-tooltip-bubble">
							<div class="tooltip-content">
								{{ tooltipText }}
							</div>
						</div>
					</div>
				</div>

				<!-- Sports Filter Tabs Pill -->
				<div v-if="!hasSportIdProp" class="sports-filter-pill">
					<button v-for="sport in availableSports" :key="sport.id" type="button" class="sport-tab-btn" :class="{ active: selectedSportId === sport.id }" :title="sport.name" @click="selectSport(sport.id)">
						<!-- Diamond emoji for "All" / Featured -->
						<span v-if="sport.icon_type === 'diamond' || sport.id === 'all'" class="diamond-icon">
							💎
						</span>

						<!-- Dynamic sport icon from pre_sports store -->
						<svg v-else-if="sport.id && sport.id !== 'all' && preSportsStore.preSportWhiteIcon"
							class="sport-svg-icon"
							xmlns="http://www.w3.org/2000/svg"
							xml:space="preserve"
							version="1.1"
							viewBox="0 0 30 25"
							xmlns:xlink="http://www.w3.org/1999/xlink"
						>
							<use :xlink:href="preSportsStore.preSportWhiteIcon(sport.id) + '#svg3108'" stroke-width="1.2px" />
						</svg>
						<span v-else class="sport-fallback-icon">🎯</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Splide Carousel Slider -->
		<div class="bet-booster-slider-wrapper">
			<Splide ref="splideRef" :key="direction + '_' + selectedSportId + '_' + (props.competition_id || '') + '_' + (props.fixture_id || '')" :options="splideOptions" class="bet-booster-splide">
				<SplideSlide v-for="boost in displayedBoosts" :key="boost.id" class="bet-booster-slide">
					<BetBoosterCard :boost="boost" :show-sport-ribbon="shouldShowCardSportRibbon" @click="handleCardClick" />
				</SplideSlide>
			</Splide>
		</div>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
.bet-booster-slide-enter-active {
	transition: max-height 0.4s cubic-bezier(0.16, 1, 0.3, 1),
				opacity 0.3s ease,
				transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
				padding 0.4s cubic-bezier(0.16, 1, 0.3, 1),
				margin 0.4s cubic-bezier(0.16, 1, 0.3, 1);
	overflow: hidden;
}

.bet-booster-slide-leave-active {
	transition: max-height 0.25s ease-in,
				opacity 0.2s ease-in,
				transform 0.25s ease-in,
				padding 0.25s ease-in,
				margin 0.25s ease-in;
	overflow: hidden;
}

.bet-booster-slide-enter-from,
.bet-booster-slide-leave-to {
	max-height: 0 !important;
	opacity: 0 !important;
	transform: translateY(-8px) scale(0.99);
	padding-top: 0 !important;
	padding-bottom: 0 !important;
	margin-top: 0 !important;
	margin-bottom: 0 !important;
}

.bet-booster-slide-enter-to,
.bet-booster-slide-leave-from {
	max-height: 500px;
	opacity: 1;
	transform: translateY(0) scale(1);
}

.bet-booster-container {
	width: 100%;
	padding: 16px 5px;
	box-sizing: border-box;
	position: relative;
	z-index: 10;

	@media (max-width: 991px) {
		padding: 8px 12px 4px 12px;
	}

	&.fixture-booster {
		padding: 16px 0 0 0;

		.bet-booster-slide {
			max-width: 290px;
		}

		@media (max-width: 991px) {
			.bet-booster-slide {
				max-width: 100%;
			}
		}

		@media (max-width: 991px) {
			margin-bottom: 12px;
			padding: 0px 8px;
		}
	}
}

/* Header */
.bet-booster-header {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	margin-bottom: 14px;
	position: relative;
	z-index: 20;

	@media (max-width: 768px) {
		margin-bottom: 8px;
	}

	.header-left {
		display: flex;
		align-content: center;
		justify-content: space-between;
		gap: 16px;
		flex-wrap: wrap;
		width: 100%;
	}

	.title-with-info {
		display: flex;
		align-items: center;
		gap: 6px;

		.bet-booster-title {
			font-size: 20px;
			font-weight: 600;
			color: var(--white);
			margin: 0;
			letter-spacing: 0.2px;

			@media (max-width: 991px) {
				font-size: 15px;
			}
		}

		.info-tooltip-container {
			position: relative;
			display: inline-flex;
			align-items: center;
			cursor: pointer;
			z-index: 30;

			.info-icon-btn {
				background: transparent;
				border: none;
				padding: 0;
				color: var(--light-gray);
				cursor: pointer;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: color 0.2s ease;

				&:hover {
					color: var(--white);
				}
			}

			.info-tooltip-bubble {
				position: absolute;
				bottom: calc(100% + 10px);
				left: -16px;
				background: var(--dark-gray);
				color: var(--white);
				padding: 10px 14px;
				border-radius: 8px;
				font-size: 11.5px;
				line-height: 1.45;
				font-weight: 500;
				width: 260px;
				box-shadow: 0 10px 28px rgba(0, 0, 0, 0.65);
				border: 1px solid var(--tab-gray);
				z-index: 99999;
				pointer-events: none;
				box-sizing: border-box;

				&::after {
					content: "";
					position: absolute;
					top: 100%;
					left: 24px;
					transform: translateX(-50%);
					border-width: 6px;
					border-style: solid;
					border-color: var(--dark-gray) transparent transparent transparent;
				}
			}
		}
	}

	.sports-filter-pill {
		display: flex;
		align-items: center;
		background: var(--dark);
		border: 1px solid rgba(255, 255, 255, 0.08);
		border-radius: 8px;
		padding: 3px;
		gap: 3px;

		.sport-tab-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 30px;
			border-radius: 6px;
			background: transparent;
			border: none;
			color: var(--light-gray);
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				color: var(--primary);
				background: var(--light-dark);

				.sport-svg-icon {
					stroke: var(--primary);
				}
			}

			&.active {
				background: var(--light-dark);
				color: var(--white);
				box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);

				.sport-svg-icon {
					stroke: var(--primary);

					use {
						stroke: var(--primary);
					}
				}
			}

			.diamond-icon {
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 13.5px;
				line-height: 1;
				filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
				transition: transform 0.2s ease;
			}

			.sport-svg-icon {
				width: 17px;
				height: 17px;
				fill: none;
				stroke: currentColor;
				display: block;
				transition: stroke 0.2s ease;
			}
		}
	}
}

/* Splide Slider */
.bet-booster-slider-wrapper {
	width: 100%;
	position: relative;

	.bet-booster-slide {
		display: flex;
		height: auto;
		width: 100%;
		max-width: 320px;

		@media (max-width: 991px) {
			max-width: 100%;
		}
	}

	:deep(.splide__slide:last-child) {
		margin-right: 0 !important;
	}

}

.bet-booster-slider-wrapper {
	:deep(.splide__arrow:disabled) {
		display: none !important;
	}

	:deep(.splide__arrows .splide__arrow--next) {
		right: -5px !important;
		display: flex;
	}

	:deep(.splide__arrows .splide__arrow--prev) {
		left: -5px !important;
		display: flex;
	}
}

html[dir="rtl"] {
	.info-tooltip-bubble {
		left: auto;
		right: -16px;

		&::after {
			left: auto;
			right: 24px;
			transform: translateX(50%);
		}
	}

	.bet-booster-slider-wrapper {
		:deep(.splide__arrows .splide__arrow--prev) {
			display: flex;
			rotate: 180deg;
			left: unset !important;
			right: -5px !important;
		}

		:deep(.splide__arrows .splide__arrow--next) {
			display: flex;
			rotate: 180deg;
			right: unset !important;
			left: -5px !important;
		}
	}
}

.formated-title {
	font-size: 16px !important;
	font-weight: 600 !important;
	color: var(--primary) !important;
	letter-spacing: 0.2px !important;
	line-height: 1.2 !important;
	margin: 0px !important;
}
</style>
